/**
 * Rekaman meeting: memecah kolom teks yang memuat beberapa rekaman, dan menjelaskan galat
 * unggahnya. Murni, dipakai tampilan lama dan rancang ulang.
 */

/**
 * Satu kolom teks, beberapa rekaman — dipecah kembali jadi bagian-bagiannya.
 *
 * Backend MENYAMBUNG rekaman kedua dan seterusnya di belakang yang pertama, dipisah
 * `── Rekaman N · kapan ──` (lihat `PEMISAH_REKAMAN`). Bentuk itu kontrak, bukan hiasan:
 * kalau layar menampilkannya mentah, rapat yang terekam tiga kali terbaca sebagai satu
 * blok panjang tanpa batas yang jelas.
 *
 * Yang pertama tidak punya kepala — itu disengaja di sisi backend, supaya meeting
 * berekaman tunggal tersimpan persis seperti sebelum penyambungan ada.
 */
const KEPALA_REKAMAN = /\n*── Rekaman (\d+) · ([^\n]*?) ──\n*/g

export function pecahRekaman(teks) {
  const isi = (teks || '').trim()
  if (!isi) return []
  const bagian = []
  let akhir = 0
  let kepala = null
  KEPALA_REKAMAN.lastIndex = 0
  for (let m = KEPALA_REKAMAN.exec(isi); m; m = KEPALA_REKAMAN.exec(isi)) {
    bagian.push({ n: kepala ? Number(kepala[1]) : 1, kapan: kepala ? kepala[2] : null,
                  isi: isi.slice(akhir, m.index).trim() })
    kepala = m
    akhir = m.index + m[0].length
  }
  bagian.push({ n: kepala ? Number(kepala[1]) : 1, kapan: kepala ? kepala[2] : null,
                isi: isi.slice(akhir).trim() })
  return bagian.filter((b) => b.isi)
}

/**
 * Ringkasan dan transkrip satu meeting, disandingkan per nomor rekaman.
 *
 * TRANSKRIPNYA SUDAH LAMA DISIMPAN dan nol kali ditampilkan — `recording_transcript`
 * ikut di `MeetingOut` sejak awal, dan layar cuma pernah membaca `recording_summary`.
 * Ringkasan menjawab "apa keputusannya"; transkrip menjawab "benarkah itu yang
 * dikatakan", dan pertanyaan kedua baru muncul justru saat yang pertama diragukan.
 *
 * Disandingkan berdasarkan NOMOR, bukan urutan larik: satu meeting bisa punya ringkasan
 * tanpa transkrip (baris lama) atau sebaliknya, dan memasangkan berdasarkan posisi akan
 * menempelkan transkrip rekaman kedua ke ringkasan rekaman pertama.
 */
export function pasangkanRekaman(ringkasan, transkrip) {
  const teks = new Map(pecahRekaman(transkrip).map((b) => [b.n, b.isi]))
  const hasil = pecahRekaman(ringkasan).map((b) => ({ ...b, transkrip: teks.get(b.n) || null }))
  // Transkrip yang TIDAK punya pasangan ringkasan tetap muncul — kalau tidak, rekaman
  // yang peringkasannya gagal terlihat seolah tidak pernah diunggah.
  for (const [n, isi] of teks) {
    if (!hasil.some((b) => b.n === n)) hasil.push({ n, kapan: null, isi: null, transkrip: isi })
  }
  return hasil.sort((a, b) => a.n - b.n)
}

/**
 * Galat unggah rekaman jadi SATU KALIMAT yang menyebut jalan keluarnya.
 *
 * Tiga sebab yang benar-benar terjadi, dan ketiganya butuh tindakan berbeda:
 * berkasnya terlalu besar (kecilkan atau lewat Telegram), saldo OpenRouter kurang
 * (top-up), atau fiturnya dimatikan tenant (nyalakan). Menampilkan ketiganya sebagai
 * "gagal" membuat yang membacanya menebak — dan dua dari tiga tebakan itu salah.
 *
 * MURNI, karena galat cuma muncul sesudah seseorang mengunggah, dan `renderToString`
 * tidak mengunggah apa pun (XXIII-1).
 */
export function jelaskanRekaman(galat, t) {
  if (!galat) return null
  if (galat.kode === 'audio_terlalu_besar') {
    const mb = Math.round((galat.mentah?.batas ?? 0) / 100_000) / 10
    return t('prospek.rekamanTerlaluBesar', { mb })
  }
  if (galat.status === 413) return t('prospek.rekamanDitolakProxy')
  if (galat.status === 402) return t('prospek.rekamanSaldoKurang')
  if (galat.status === 409) return t('prospek.rekamanFiturMati')
  return galat.message || t('umum.gagal')
}
